/**
 * The explanation the backend stores on an automatically generated diet or
 * salary day (``generation_trace``). It arrives as a JSON string.
 */
export interface GenerationTrace {
  rule: string
  salaryWorkTimeName: string
  /** The value the row was written with, after every modifier. */
  value: number
  /** What the rule gave before any modifier changed it. */
  baseValue: number
  perWorkPeriod?: {
    amount: number
    perHours: number | null
    rounding: 'floor' | 'ceil' | null
    blocks: number | null
  }
  chain?: {
    start: Date
    end: Date
    blocks: number
    value: number
    owned: { start: Date; end: Date } | null
    ownedBlocks: number
    actions: GenerationTraceChainAction[]
  }
  /** Set on a row paid for a rest rather than a work period. */
  rest?: { start: Date; end: Date }
  modifiers: GenerationTraceModifier[]
}

export interface GenerationTraceModifier {
  name: string
  award: 'award' | 'deny' | null
  amount: number | null
  perHours: number | null
  rounding: 'floor' | 'ceil' | null
  maxAmount: number | null
  amountIgnored: boolean
  salaryCodeChanged: boolean
}

export interface GenerationTraceChainAction {
  name: string
  action: 'break' | 'continue'
}

interface RawModifier {
  name?: string
  award?: 'award' | 'deny'
  amount?: string
  per_minutes?: number
  rounding?: 'floor' | 'ceil'
  max_amount?: string
  ignored?: string
  salary_code_id?: number
}

interface RawTrace {
  rule?: string
  salary_work_time_name?: string
  value?: string
  base_value?: string
  amount?: string
  per_minutes?: number
  rounding?: 'floor' | 'ceil'
  blocks?: number
  covered?: boolean
  chain?: {
    start: string
    end: string
    blocks: number
    value: string
    actions?: { name?: string; chain_action?: 'break' | 'continue' }[]
  }
  owned?: { start: string; end: string }
  rest?: { start: string; end: string }
  modifiers?: RawModifier[]
}

export function parseGenerationTrace(raw: unknown): GenerationTrace | null {
  const trace = toObject(raw)
  if (!trace?.rule) return null

  const parsed: GenerationTrace = {
    rule: trace.rule,
    salaryWorkTimeName: trace.salary_work_time_name ?? '',
    value: Number(trace.value ?? 0),
    baseValue: Number(trace.base_value ?? trace.value ?? 0),
    modifiers: (trace.modifiers ?? []).map(parseModifier),
  }
  if (trace.rest) {
    parsed.rest = {
      start: new Date(trace.rest.start),
      end: new Date(trace.rest.end),
    }
  }
  if (trace.chain) {
    parsed.chain = {
      start: new Date(trace.chain.start),
      end: new Date(trace.chain.end),
      blocks: trace.chain.blocks,
      value: Number(trace.chain.value),
      owned: trace.owned
        ? { start: new Date(trace.owned.start), end: new Date(trace.owned.end) }
        : null,
      ownedBlocks: trace.covered ? 0 : trace.blocks ?? 0,
      actions: uniqueActions(trace.chain.actions ?? []),
    }
  } else if (!trace.rest) {
    // A rest outside any chain has no base value: a modifier awards it.
    parsed.perWorkPeriod = {
      amount: Number(trace.amount ?? 0),
      perHours: trace.per_minutes ? trace.per_minutes / 60 : null,
      rounding: trace.rounding ?? null,
      blocks: trace.blocks ?? null,
    }
  }
  return parsed
}

function parseModifier(raw: RawModifier): GenerationTraceModifier {
  return {
    name: raw.name ?? '',
    award: raw.award ?? null,
    amount: raw.amount !== undefined ? Number(raw.amount) : null,
    perHours: raw.per_minutes ? raw.per_minutes / 60 : null,
    rounding: raw.rounding ?? null,
    maxAmount: raw.max_amount !== undefined ? Number(raw.max_amount) : null,
    amountIgnored: raw.ignored === 'amount_on_chain_rule',
    salaryCodeChanged: raw.salary_code_id !== undefined,
  }
}

function uniqueActions(
  raw: { name?: string; chain_action?: 'break' | 'continue' }[]
): GenerationTraceChainAction[] {
  const seen = new Set<string>()
  const actions: GenerationTraceChainAction[] = []
  for (const entry of raw) {
    if (!entry.chain_action) continue
    const key = `${entry.name}:${entry.chain_action}`
    if (seen.has(key)) continue
    seen.add(key)
    actions.push({ name: entry.name ?? '', action: entry.chain_action })
  }
  return actions
}

function toObject(raw: unknown): RawTrace | null {
  if (!raw) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as RawTrace
    } catch {
      return null
    }
  }
  return typeof raw === 'object' ? (raw as RawTrace) : null
}
